import { useTranslation } from 'react-i18next';
import type { IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { useLabel } from '@platform/hooks/useLabel';
import { moduleNamespace } from '@platform/i18n/moduleBundle';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { mainRecordTypeOf } from '@platform/engine/menu/moduleSections';
import { modelSourcesOf } from '../design/dataModel';
import type { IRecordsRow } from './IRecordsRow';

/**
 * Рядки «Записів і форм» модуля (прототип v4 cdRecords / cdForms: [основний тип, ...MOD_ENTITIES[модуль]]): основний тип, далі сутності
 * прототипу головного типу (`designEntities` без `extra`, рядки пакета `entities.<тип>.designEntities.<i>`) — підсвічуються за типом
 * записів сутності чи її англійською назвою (`?focus=` пунктів «Таблиці»); опис форми сутності — рядок пакета `….formDescription` (текст
 * прототипу), інакше загальний; визначення без сутностей — інші неприховані типи записів. Дочірня таблиця (`page`, окремий модуль
 * прототипу) — вона як основний тип і лише її сутності.
 */
export function useRecordsRows(module: IModuleDefinition, page?: IRecordTypeDefinition): IRecordsRow[] {
  const { t } = useTranslation();
  const label = useLabel();
  const strings = useModuleStrings(module.module.key);
  const main = page ?? mainRecordTypeOf(module);
  const own = main?.designEntities ?? findModuleDefinition(module.module.key)?.recordTypes.find((r) => r.key === main?.key)?.designEntities;
  const sources = modelSourcesOf(module, own);
  const { main: head, entities, types } = page ? { main: page, entities: sources.entities, types: [] } : sources;
  if (!head) return [];
  const ns = moduleNamespace(module.module.key);
  const typeRow = (rt: typeof head, isMain: boolean): IRecordsRow => ({
    key: rt.key, main: isMain, focus: [rt.key],
    name: strings.entity(rt.key, 'singular', rt.singular ?? null) || strings.entity(rt.key, 'name', rt.name),
    description: strings.entity(rt.key, 'description', rt.description ?? null),
    formDescription: t(isMain ? 'modSettings.records.mainFormDesc' : 'modSettings.records.childFormDesc', { name: (rt.singular?.en ?? rt.name.en ?? '').toLowerCase() }),
  });
  return [typeRow(head, true), ...(entities ?? []).map(({ entity, index }): IRecordsRow => {
    const at = `entities.${head.key}.designEntities.${index}`;
    return {
      key: `e${index}`, main: false, focus: [entity.recordType ?? '', entity.name.en ?? ''].filter(Boolean),
      name: t(`${at}.name`, { ns, defaultValue: label(entity.name) }),
      description: entity.description ? t(`${at}.description`, { ns, defaultValue: label(entity.description) }) : '',
      formDescription: t(`${at}.formDescription`, { ns, defaultValue: t('modSettings.records.childFormDesc', { name: (entity.name.en ?? '').toLowerCase() }) }),
    };
  }), ...types.map((rt) => typeRow(rt, false))];
}
